const { getElements } = window.BAO.Utils

const escapeHtml = value =>
  String(value ?? '').replace(
    /[&<>"']/g,
    char =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[char]),
  )

class WishlistPage extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.populateLists = this.populateLists.bind(this)
    this.handleWishlistChange = this.handleWishlistChange.bind(this)
    this.setupWishlist = this.setupWishlist.bind(this)

    this.handleRemoveItem = this.handleRemoveItem.bind(this)

    this.getCurrentWishlistDetails = this.getCurrentWishlistDetails.bind(this)

    this.wishlistError = this.wishlistError.bind(this)

    this.listeners.add(document, 'click', this.handleWishlistChange)
    this.listeners.add(document, 'click', this.handleRemoveItem)

    this.handleBack = this.handleBack.bind(this)
    this.els.back.elements.forEach(backEl => {
      this.listeners.add(backEl, 'click', this.handleBack)
    })

    window.BAO.Wysh.ready().then(isReady => {
      if (isReady !== true) return this.wishlistError('Wysh is not available')

      this.handleBack()
    })
  }

  get isCustomer () {
    return this.hasAttribute('data-is-customer')
  }

  async populateLists (lists) {
    this.els.delete.element.setAttribute(
      'aria-hidden',
      this.isCustomer === false || lists.length < 2,
    )

    this.els.wishlists.element.innerHTML = ''

    if (lists.length > 0) {
      this.els['current-wishlist-text'].element.setAttribute(
        'data-wishlist-id',
        lists[0].wishlist_key,
      )

      const homeElements = await Promise.all(
        lists.map(list => this.createHomeElement(list)),
      )

      this.els.wishlists.element.innerHTML = homeElements.join('')

      this.els['page-loading'].element.setAttribute('aria-hidden', true)
      this.els['page-home'].element.setAttribute('aria-hidden', false)
      this.els['page-empty'].element.setAttribute('aria-hidden', true)
      this.els['page-wishlist'].element.setAttribute('aria-hidden', true)
    } else {
      this.els['page-loading'].element.setAttribute('aria-hidden', true)
      this.els['page-home'].element.setAttribute('aria-hidden', true)
      this.els['page-empty'].element.setAttribute('aria-hidden', false)
      this.els['page-wishlist'].element.setAttribute('aria-hidden', true)
    }
  }

  async createHomeElement (list) {
    let cardImages = ''

    if (list.total > 0) {
      const details = await window.BAO.Wysh.getDetails(list.wishlist_key, {
        perPage: 3,
      }).catch(() => null)

      cardImages = '<div class="wsh-Card_Images">'
      details?.items.slice(0, 3).forEach(item => {
        if (item.data.image) {
          cardImages += `<img class="wsh-Card_Image" src="${escapeHtml(
            item.data.image,
          )}" alt="${escapeHtml(item.data.title)}">`
        }
      })

      cardImages += '</div>'
    }

    return `<div class="wsh-Card">
      <button
        type="button"
        class="wsh-Card_Title fz-18_144"
        data-wishlist-page-el="wishlist-change-trigger"
        data-wishlist-id="${escapeHtml(list.wishlist_key)}"
      >
        ${escapeHtml(list.name || window.theme.wishlist.title)}
      </button>
      <p class="wsh-Card_Text fz-14_171">${list.total} ${
      list.total === 1
        ? window.theme.wishlist.item
        : window.theme.wishlist.items
    }</p>

      ${cardImages}

      <div class="wsh-Card_Buttons">
      ${
        list.total === 0
          ? `<a href="/collections/all" class="wsh-Card_Button btn-Button btn-Button-outline">${window.theme.wishlist.addToWishlist}</a>`
          : `<button type="button" class="wsh-Card_Button btn-Button btn-Button-outline" data-wishlist-page-el="wishlist-change-trigger" data-wishlist-id="${escapeHtml(
              list.wishlist_key,
            )}">
            ${window.theme.wishlist.viewWishlist}
          </button>`
      }
      </div>
    </div>`
  }

  handleWishlistChange (event) {
    const trigger = event.target.closest(
      '[data-wishlist-page-el="wishlist-change-trigger"]',
    )

    if (!trigger) return

    this.els['current-wishlist-text'].element.setAttribute(
      'data-wishlist-id',
      trigger.dataset.wishlistId,
    )

    this.getCurrentWishlistDetails()

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  getCurrentWishlistDetails () {
    return window.BAO.Wysh.getAllItems(
      this.els['current-wishlist-text'].element.dataset.wishlistId,
    )
      .then(this.setupWishlist)
      .catch(this.wishlistError)
  }

  async setupWishlist (wishlist) {
    this.els.delete.element.setAttribute(
      'data-wishlist-id',
      wishlist.wishlist_key,
    )

    getElements('[data-wishlist-page-el="wishlist-change-trigger"]').forEach(
      el => {
        el.setAttribute(
          'aria-current',
          el.dataset.wishlistId === wishlist.wishlist_key,
        )
      },
    )

    const productCardHTML = await this.getAllProductCardHTML(wishlist)
    this.els['current-wishlist-text'].element.textContent = `${wishlist.name ||
      window.theme.wishlist.title} (${this.currentWishlistCount})`
    this.els.products.element.innerHTML = productCardHTML

    if (wishlist.items.length > 0) {
      this.els['panel-list'].element.setAttribute('aria-hidden', false)
      this.els['panel-empty'].element.setAttribute('aria-hidden', true)
    } else {
      this.els['panel-list'].element.setAttribute('aria-hidden', true)
      this.els['panel-empty'].element.setAttribute('aria-hidden', false)
    }

    this.els['page-loading'].element.setAttribute('aria-hidden', true)
    this.els['page-home'].element.setAttribute('aria-hidden', true)
    this.els['page-empty'].element.setAttribute('aria-hidden', true)
    this.els['page-wishlist'].element.setAttribute('aria-hidden', false)
  }

  async getAllProductCardHTML (wishlist) {
    const productCardHTML = await Promise.all(
      wishlist.items.map(
        async (product, index) => await this.getProductCardHTML(product, index),
      ),
    )

    this.currentWishlistCount = productCardHTML.filter(Boolean).length
    return productCardHTML.filter(Boolean).join('')
  }

  async getProductCardHTML (product, index) {
    return window.BAO.sectionRenderer
      .fetchSingle('wishlist-product-card', window.BAO.Wysh.itemUrl(product))
      .then(res => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
        const myself = newDOM.querySelector('article')

        if (myself) {
          const itemClasses =
            (index + 1) % 2
              ? 'wsh-Page_Item wsh-Page_Item-left'
              : 'wsh-Page_Item wsh-Page_Item-right'

          return `<div class="${itemClasses}">${myself.outerHTML.replace(
            'data-wishlist-page-el="remove"',
            `data-wishlist-page-el="remove" data-product-id="${product.product_id}" data-variant-id="${product.variant_id}"`,
          )}</div>`
        }
      })
      .catch(() => {
        return null
      })
  }

  handleRemoveItem (event) {
    const trigger = event.target.closest('[data-wishlist-page-el="remove"]')

    if (!trigger) return

    window.BAO.Wysh.remove({
      productId: trigger.dataset.productId,
      variantId: trigger.dataset.variantId,
      wishlistKey: this.els['current-wishlist-text'].element.dataset.wishlistId,
    })
      .then(this.getCurrentWishlistDetails)
      .catch(this.wishlistError)
  }

  handleBack () {
    window.BAO.Wysh.getLists()
      .then(this.populateLists)
      .catch(this.wishlistError)
  }

  wishlistError (error) {
    // eslint-disable-next-line
    console.log(`Wishlist error: ${window.BAO.Wysh.errorMessage(error)}`)

    this.els['page-loading'].element.setAttribute('aria-hidden', true)
    this.els['page-home'].element.setAttribute('aria-hidden', true)
    this.els['page-empty'].element.setAttribute('aria-hidden', false)
    this.els['page-wishlist'].element.setAttribute('aria-hidden', true)
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage)
}
